/**
 * Presentation Settings modal — matches the XD design.
 *
 * Drag-and-drop: the entire row is the drag trigger. A DragOverlay renders a
 * floating "ghost" while dragging so the animation is smooth and the list
 * shifts without flickering. Checkbox/label stop pointer-down propagation so
 * clicking them toggles without starting a drag.
 */

import { useEffect, useState, useMemo } from 'react'
import { GripVertical, X, Check } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { dashboardApi } from '../../lib/dashboardApi'

const TRANSITIONS = [
  { value: 'random', label: 'Random' },
  { value: 'fade',   label: 'Fade'   },
  { value: 'wipe',   label: 'Wipe'   },
  { value: 'slide',  label: 'Slide'  },
  { value: 'push',   label: 'Push'   },
  { value: 'mosaic', label: 'Mosaic' },
  { value: 'expand', label: 'Expand' },
  { value: 'shred',  label: 'Shred'  },
  { value: 'blind',  label: 'Blind'  },
]

const MIN_PAUSE_SECONDS = 30

// ─── Ghost row shown inside DragOverlay while dragging ───────────────────────
function GhostRow({ dashboard }) {
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 bg-white border border-[#358ECD] rounded-md shadow-xl ring-1 ring-[#358ECD]/30 opacity-95">
      <GripVertical size={16} className="text-[#358ECD] flex-shrink-0" aria-hidden="true" />
      <span className="h-4 w-4 flex-shrink-0 rounded border-2 border-[#358ECD] bg-white" />
      <span className="flex-1 text-sm text-gray-800">{dashboard.name}</span>
    </div>
  )
}

// ─── Sortable row ────────────────────────────────────────────────────────────
function SortableRow({ dashboard, onToggle, isDragActive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dashboard.id })

  // While this row is being dragged, render a transparent placeholder so the
  // list reflows correctly. The actual dragged look comes from DragOverlay.
  const style = {
    transform: CSS.Transform.toString(transform),
    // Only animate the transition when we're NOT the active item — the active
    // item is invisible (opacity 0) and we don't want it animating.
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0 : 1,
    cursor: isDragActive ? (isDragging ? 'grabbing' : 'grab') : 'grab',
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 py-2.5 px-3 select-none hover:bg-gray-50 rounded"
      {...attributes}
      {...listeners}
    >
      <GripVertical
        size={16}
        className="text-gray-300 flex-shrink-0 pointer-events-none"
        aria-hidden="true"
      />
      {/* Stop propagation so click → toggle checkbox, not start drag */}
      <input
        id={`pres-dash-${dashboard.id}`}
        type="checkbox"
        checked={dashboard.included}
        onChange={(e) => onToggle(dashboard.id, e.target.checked)}
        onPointerDown={(e) => e.stopPropagation()}
        className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#358ECD]
                   focus:ring-[#358ECD] cursor-pointer"
      />
      <label
        htmlFor={`pres-dash-${dashboard.id}`}
        className="flex-1 text-sm text-gray-800 cursor-pointer"
        onPointerDown={(e) => e.stopPropagation()}
      >
        {dashboard.name}
      </label>
    </li>
  )
}

// ─── Modal ───────────────────────────────────────────────────────────────────
export default function PresentationSettingsModal({ open, projectId, onClose, onSaved }) {
  const [loading,  setLoading]  = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [saved,    setSaved]    = useState(false)
  const [error,    setError]    = useState(null)

  const [pauseSeconds, setPauseSeconds] = useState(MIN_PAUSE_SECONDS)
  const [pauseError,   setPauseError]   = useState(null)
  const [randomOrder,  setRandomOrder]  = useState(false)
  const [transition,   setTransition]   = useState('fade')
  const [dashboards,   setDashboards]   = useState([])

  // Track which item is actively being dragged (for GhostRow in DragOverlay)
  const [activeId, setActiveId] = useState(null)
  const activeDashboard = dashboards.find((d) => d.id === activeId) || null

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Re-fetch every open so the modal always shows fresh state.
  useEffect(() => {
    if (!open || !projectId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setSaved(false)
    setPauseError(null)
    dashboardApi
      .getPresentationConfig(projectId)
      .then((res) => {
        if (cancelled) return
        const cfg = res.data
        setPauseSeconds(Math.max(MIN_PAUSE_SECONDS, cfg.pause_seconds || MIN_PAUSE_SECONDS))
        setRandomOrder(!!cfg.random_order)
        setTransition(cfg.transition || 'fade')
        setDashboards(cfg.dashboards || [])
      })
      .catch((err) => {
        if (!cancelled)
          setError(err.response?.data?.detail || 'Failed to load settings.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, projectId])

  const includedCount = useMemo(
    () => dashboards.filter((d) => d.included).length,
    [dashboards],
  )

  // ── Drag handlers ──────────────────────────────────────────────────────────
  function handleDragStart(event) {
    setActiveId(event.active.id)
  }

  function handleDragEnd(event) {
    const { active, over } = event
    setActiveId(null)
    if (!over || active.id === over.id) return
    setDashboards((rows) => {
      const oldIdx = rows.findIndex((r) => r.id === active.id)
      const newIdx = rows.findIndex((r) => r.id === over.id)
      return arrayMove(rows, oldIdx, newIdx)
    })
  }

  function handleDragCancel() {
    setActiveId(null)
  }

  function handleToggle(id, checked) {
    setDashboards((rows) =>
      rows.map((r) => (r.id === id ? { ...r, included: checked } : r)),
    )
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    const seconds = Number(pauseSeconds)
    if (!seconds || seconds < MIN_PAUSE_SECONDS) {
      setPauseError(`Minimum ${MIN_PAUSE_SECONDS} seconds required.`)
      return
    }
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const payload = {
        pause_seconds: seconds,
        random_order: randomOrder,
        transition,
        dashboards: dashboards.map((d) => ({ id: d.id, included: d.included })),
      }
      const res = await dashboardApi.savePresentationConfig(projectId, payload)
      if (onSaved) onSaved(res.data)
      setSaved(true)
      setTimeout(() => {
        onClose()
        setTimeout(() => setSaved(false), 300)
      }, 900)
    } catch (err) {
      const data = err.response?.data
      setError(
        typeof data === 'string'
          ? data
          : data?.detail ||
              Object.entries(data || {})
                .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                .join(' ') ||
              'Failed to save settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh]
                   overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="presentation-settings-title"
      >
        {/* Header */}
        <header className="flex items-start justify-between px-6 pt-5 pb-3 border-b">
          <h2
            id="presentation-settings-title"
            className="common-head25 text-lg font-bold text-[#4D4D4D]"
          >
            Presentation Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 -mr-1"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded
                            text-sm text-red-700">
              {error}
            </div>
          )}
          {saved && (
            <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded
                            text-sm text-green-700 flex items-center gap-2">
              <Check size={14} />
              Settings saved successfully!
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
          ) : (
            <>
              {/* Presentation Mode */}
              <section className="mb-5">
                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-2">
                  Presentation Mode
                </h3>
                <div className="flex items-center gap-2 text-sm text-[#4D4D4D]">
                  <label htmlFor="pres-pause" className="common-size15">Cycle dashboards every</label>
                  <input
                    id="pres-pause"
                    type="number"
                    min={MIN_PAUSE_SECONDS}
                    value={pauseSeconds}
                    onChange={(e) => {
                      const val = e.target.value
                      setPauseSeconds(val)
                      const n = Number(val)
                      if (val === '' || n < MIN_PAUSE_SECONDS) {
                        setPauseError(`Minimum ${MIN_PAUSE_SECONDS} seconds required.`)
                      } else {
                        setPauseError(null)
                      }
                    }}
                    onBlur={(e) => {
                      const n = Number(e.target.value)
                      if (!e.target.value || n < MIN_PAUSE_SECONDS) {
                        setPauseError(`Minimum ${MIN_PAUSE_SECONDS} seconds required.`)
                      }
                    }}
                    className={`w-16 px-2 py-1 border rounded text-center focus:outline-none focus:ring-1
                      ${pauseError
                        ? 'border-red-400 focus:ring-red-400'
                        : 'border-gray-300 focus:ring-[#358ECD]'}`}
                  />
                  <span className="text-[#4D4D4D] common-size15">seconds (minimum: {MIN_PAUSE_SECONDS} seconds)</span>
                </div>
                {pauseError && (
                  <p className="common-size15 mt-1.5 text-xs text-red-600">{pauseError}</p>
                )}
              </section>

              {/* Select Dashboards */}
              <section className="mb-5">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="common-size18 text-sm font-semibold text-[#4D4D4D]">
                    Select Dashboards
                  </h3>
                  <span className="common-size15 text-xs text-[#888888]">
                    Check to include · Drag row to reorder
                  </span>
                </div>

                {dashboards.length === 0 ? (
                  <p className="common-size15 text-sm text-[#888888] py-3">
                    No dashboards available for this project.
                  </p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                  >
                    <SortableContext
                      items={dashboards.map((d) => d.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul className="border-[#D6D6D6] divide-y divide-gray-100 border-b present-list">
                        {dashboards.map((d) => (
                          <SortableRow
                            key={d.id}
                            dashboard={d}
                            onToggle={handleToggle}
                            isDragActive={activeId !== null}
                          />
                        ))}
                      </ul>
                    </SortableContext>

                    {/* Smooth floating ghost while dragging */}
                    <DragOverlay dropAnimation={null}>
                      {activeDashboard ? (
                        <GhostRow dashboard={activeDashboard} />
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )}

                {includedCount > 0 && (
                  <p className="common-size15 mt-1.5 text-xs text-gray-500">
                    {includedCount} dashboard{includedCount === 1 ? '' : 's'} in rotation.
                  </p>
                )}
              </section>

              {/* Randomize */}
              <section className="mb-5">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={randomOrder}
                    onChange={(e) => setRandomOrder(e.target.checked)}
                    className="common-size15 h-4 w-4 rounded border-gray-300 text-[#4D4D4D]"
                  />
                  Randomize Presentation Order
                </label>
              </section>

              {/* Transition Effects */}
              <section>
                <h3 className="common-size18 text-sm font-semibold text-[#4D4D4D] mb-2">
                  Transition Effects
                </h3>
                <select
                  value={transition}
                  onChange={(e) => setTransition(e.target.value)}
                  className="w-full px-3 py-2 border bg-white border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-1 focus:ring-[#358ECD]"
                >
                  {TRANSITIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="px-6 py-4 border-t bg-white">
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving || saved || !!pauseError}
            className={`common-size18 w-full text-white text-sm font-bold uppercase tracking-wide
                        py-2.5 rounded transition-colors disabled:opacity-60
                        disabled:cursor-not-allowed flex items-center justify-center gap-2
                        ${saved ? 'bg-green-600 hover:bg-green-700' : 'bg-[#358ECD] hover:bg-[#2c7ab2]'}`}
          >
            {saving ? 'Saving…' : saved ? <><Check size={16} />Saved!</> : 'Save'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="common-size16 mt-2 w-full text-center text-sm text-[#358ECD] font-semibold hover:underline disabled:opacity-50"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  )
}
